import React, {useCallback, useEffect, useRef, useState} from "react"
import {useSearchParams} from "react-router-dom"
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormControlLabel,
	IconButton,
	InputLabel,
	MenuItem,
	Pagination,
	Select,
	SelectChangeEvent,
	Skeleton,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material"
import AccessTimeIcon from "@mui/icons-material/AccessTime"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import FileDownloadIcon from "@mui/icons-material/FileDownload"
import LinkIcon from "@mui/icons-material/Link"
import RefreshIcon from "@mui/icons-material/Refresh"
import styled from "styled-components"
import {format, formatDistanceToNow} from "date-fns"
import JSZip from "jszip"
import {pdf} from "@react-pdf/renderer"

import {AddReminderButton} from "./AddReminderButton"
import {CasePDFDocument, CasePDFData} from "./CasePDF"
import {NoteItemCard} from "./CustomerPage/NotesV2"
import {CasesExportPanel} from "./CasesExportPanel"
import {PickUpNextCaseButton} from "./PickUpNextCaseButton"

import {useAgent} from "@indebted/hooks/Agent"
import {
	api,
	CaseNote,
	CasesResponse,
	ClientCase,
	ClientMyCase,
	ClientCasesCaseType,
	ClientCasesClient,
	ClientCasesExportColumn,
	ClientCasesExportType,
	ClientCasesSortOption,
} from "@indebted/api"
import {BackToSearch} from "@indebted/components/BackToSearch"
import {Layout} from "@indebted/components/Layout"
import {useNotification} from "@indebted/components/Notification"
import {colors} from "@indebted/theme/colors"

type FilterOptions = {
	clients: ClientCasesClient[]
	caseTypes: ClientCasesCaseType[]
	sortOptions: ClientCasesSortOption[]
}

type CasesState = {
	cases: ClientCase[]
	total: number
	capped: boolean
	loading: boolean
	error: string
}

function CasesPage() {
	const [agentEmail] = useAgent()
	const [searchParams, setSearchParams] = useSearchParams()
	const clientID = searchParams.get("clientID") ?? ""
	const caseType = searchParams.get("caseType") ?? ""
	const sortType = searchParams.get("sortType") ?? "oldest"
	const page = parseInt(searchParams.get("page") ?? "1", 10)

	const PAGE_SIZE = 20

	const [refreshKey, setRefreshKey] = useState(0)

	const [options, setOptions] = useState<FilterOptions | null>(null)
	const optionsLoaded = useRef(false)

	const [exportConfig, setExportConfig] = useState<{
		enabled: boolean
		types: ClientCasesExportType[]
		columns: ClientCasesExportColumn[]
		maxDateRange: number
	} | null>(null)

	const [caseOwnershipEnabled, setCaseOwnershipEnabled] = useState(false)
	const [myCases, setMyCases] = useState<ClientMyCase[]>([])

	const [casesState, setCasesState] = useState<CasesState>({
		cases: [],
		total: 0,
		capped: false,
		loading: true,
		error: "",
	})

	useEffect(() => {
		setCasesState((prev) => ({...prev, loading: true, error: ""}))
		api.cases
			.list({
				clientID: clientID || undefined,
				caseType: caseType || undefined,
				sortType: sortType || undefined,
				agentEmail: agentEmail || undefined,
			})
			.then((data) => {
				if (!optionsLoaded.current) {
					setOptions({clients: data.Clients, caseTypes: data.CaseTypes, sortOptions: data.SortOptions})
					setExportConfig({
						enabled: data.ExportEnabled,
						types: data.ExportTypes,
						columns: data.ExportColumns,
						maxDateRange: data.MaxDateRange,
					})
					setCaseOwnershipEnabled(data.CaseOwnershipEnabled)
					optionsLoaded.current = true
				}
				setMyCases(data.MyCases)
				setCasesState({cases: data.Cases, total: data.Total, capped: data.Capped, loading: false, error: ""})
			})
			.catch(() => setCasesState((prev) => ({...prev, loading: false, error: "Failed to load cases"})))
	}, [clientID, caseType, sortType, refreshKey, agentEmail])

	const setFilter = (key: string, value: string) => {
		const next = new URLSearchParams(searchParams)
		if (value) {
			next.set(key, value)
		} else {
			next.delete(key)
		}
		next.delete("page")
		setSearchParams(next)
	}

	const setPage = (newPage: number) => {
		const next = new URLSearchParams(searchParams)
		if (newPage <= 1) {
			next.delete("page")
		} else {
			next.set("page", String(newPage))
		}
		setSearchParams(next)
		window.scrollTo({top: 0, behavior: "smooth"})
	}

	const totalPages = casesState.cases.length > 0 ? Math.ceil(casesState.cases.length / PAGE_SIZE) : 1
	const safePage = Math.max(1, Math.min(page, totalPages))

	return (
		<Layout>
			<Box px={3} pt={3} mb={2}>
				<BackToSearch backURL="/" />
			</Box>
			<Box px={3} pb={4}>
				<Box display="flex" gap={2} mb={3} flexWrap="wrap" alignItems="center">
					<FormControl size="small" sx={{minWidth: 220}} disabled={!options}>
						<InputLabel shrink>Client</InputLabel>
						<Select
							label="Client"
							value={clientID}
							onChange={(e: SelectChangeEvent) => setFilter("clientID", e.target.value)}
							displayEmpty
						>
							<MenuItem value="">All clients</MenuItem>
							{options?.clients.map((c) => (
								<MenuItem key={c.ID} value={c.ID}>
									{c.Name} ({c.CountryCode})
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<FormControl size="small" sx={{minWidth: 200}} disabled={!options}>
						<InputLabel shrink>Case type</InputLabel>
						<Select
							label="Case type"
							value={caseType}
							onChange={(e: SelectChangeEvent) => setFilter("caseType", e.target.value)}
							displayEmpty
						>
							<MenuItem value="">All types</MenuItem>
							{options?.caseTypes.map((ct) => (
								<MenuItem key={ct.ID} value={ct.ID}>
									{ct.Label}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<FormControl size="small" sx={{minWidth: 200}} disabled={!options}>
						<InputLabel>Sort</InputLabel>
						<Select
							label="Sort"
							value={sortType}
							onChange={(e: SelectChangeEvent) => setFilter("sortType", e.target.value)}
						>
							{options?.sortOptions.map((s) => (
								<MenuItem key={s.ID} value={s.ID}>
									{s.Label}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<Tooltip title="Refresh">
						<span>
							<IconButton
								onClick={() => setRefreshKey((k) => k + 1)}
								disabled={casesState.loading}
								sx={{
									transition: "transform 0.4s",
									...(casesState.loading && {transform: "rotate(360deg)"}),
								}}
							>
								<RefreshIcon />
							</IconButton>
						</span>
					</Tooltip>

					<Box sx={{ml: "auto"}} display="flex" gap={2} alignItems="center">
						{exportConfig?.enabled && options && (
							<CasesExportPanel
								exportTypes={exportConfig.types}
								exportColumns={exportConfig.columns}
								maxDateRange={exportConfig.maxDateRange}
								clients={options.clients}
								caseTypes={options.caseTypes}
							/>
						)}
						{caseOwnershipEnabled && (
							// The list projection is eventually consistent, so the
							// refresh waits for the assignment to land, like
							// refreshCustomerData(5000).
							<PickUpNextCaseButton
								onPickedUp={() => setTimeout(() => setRefreshKey((k) => k + 1), 5000)}
							/>
						)}
					</Box>
				</Box>

				{caseOwnershipEnabled && myCases.length > 0 && (
					<>
						<Box mb={3}>
							<Typography variant="subtitle1" fontWeight={600} mb={2}>
								My cases ({myCases.length})
							</Typography>
							<Box display="flex" flexDirection="column" gap={2}>
								{myCases.map((c) => (
									<CaseCard key={c.CaseID} case={c} pickedUpAt={c.LastAssignedAt} />
								))}
							</Box>
						</Box>
						<Divider sx={{mb: 3}} />
						<Typography variant="subtitle1" fontWeight={600} mb={2}>
							All open cases
						</Typography>
					</>
				)}

				{casesState.error && (
					<Typography color="error" mb={2}>
						{casesState.error}
					</Typography>
				)}

				{!casesState.error && (
					<>
						{!casesState.loading && (
							<Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
								<Typography variant="body2" color="text.secondary">
									{casesState.total > casesState.cases.length
										? `Showing first ${casesState.cases.length} of ${casesState.total}${casesState.capped ? "+" : ""} active cases`
										: `${casesState.total} active case${casesState.total !== 1 ? "s" : ""}`}
								</Typography>
								{casesState.cases.length > PAGE_SIZE && (
									<Pagination
										count={totalPages}
										page={safePage}
										onChange={(_, value) => setPage(value)}
										color="primary"
										size="small"
									/>
								)}
							</Box>
						)}

						{casesState.loading && (
							<Box display="flex" flexDirection="column" gap={2}>
								{[0, 1, 2, 3, 4].map((i) => (
									<CaseCardSkeleton key={i} />
								))}
							</Box>
						)}

						{!casesState.loading && casesState.cases.length === 0 && (
							<Typography color="text.secondary">No cases found</Typography>
						)}

						<Box display="flex" flexDirection="column" gap={2}>
							{!casesState.loading &&
								casesState.cases
									.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)
									.map((c: ClientCase) => <CaseCard key={c.CaseID} case={c} />)}
						</Box>

						{!casesState.loading && casesState.cases.length > PAGE_SIZE && (
							<Box display="flex" justifyContent="center" mt={3}>
								<Pagination
									count={totalPages}
									page={safePage}
									onChange={(_, value) => setPage(value)}
									color="primary"
								/>
							</Box>
						)}
					</>
				)}
			</Box>
		</Layout>
	)
}

function CaseCardSkeleton() {
	return (
		<CaseCardContainer>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
				<Box display="flex" alignItems="center" gap={1}>
					<Skeleton variant="rounded" width={100} height={22} />
					<Skeleton variant="text" width={120} />
				</Box>
				<Box display="flex" alignItems="center" gap={1}>
					<Skeleton variant="rounded" width={28} height={28} />
					<Skeleton variant="rounded" width={90} height={22} />
					<Skeleton variant="rounded" width={20} height={20} />
				</Box>
			</Box>
			<Skeleton variant="text" width={160} sx={{mb: 1}} />
			<Skeleton variant="text" width={200} />
			<Skeleton variant="text" width={120} sx={{mb: 1.5}} />
			<Skeleton variant="rounded" height={36} />
		</CaseCardContainer>
	)
}

function CaseCard({case: c, pickedUpAt}: {case: ClientCase; pickedUpAt?: string}) {
	const {notification} = useNotification()
	const [agentEmail] = useAgent()
	const [notes, setNotes] = useState<CaseNote[]>([])
	const [notesLoading, setNotesLoading] = useState(false)
	const [pdfDialogOpen, setPdfDialogOpen] = useState(false)
	const [pdfNotesLoading, setPdfNotesLoading] = useState(false)
	const [pdfNotes, setPdfNotes] = useState<CaseNote[]>([])
	const [pdfData, setPdfData] = useState<CasePDFData | null>(null)
	const [pdfDescription, setPdfDescription] = useState("")
	const [selectedNoteIDs, setSelectedNoteIDs] = useState<Set<string>>(new Set())
	const [exporting, setExporting] = useState(false)

	const loadNotes = useCallback(() => {
		setNotesLoading(true)
		api.customer
			.getCaseNotes(c.CustomerID, c.CaseID)
			.then((data) => setNotes(data))
			.catch(() => notification.error("Failed to load notes"))
			.finally(() => setNotesLoading(false))
	}, [c.CustomerID, c.CaseID, notification])

	const openPdfDialog = (e: React.MouseEvent) => {
		e.stopPropagation()
		setPdfDialogOpen(true)
		setPdfData(null)
		setPdfNotesLoading(true)
		const notesPromise =
			notes.length > 0 ? Promise.resolve(notes) : api.customer.getCaseNotes(c.CustomerID, c.CaseID)
		Promise.all([notesPromise, api.customer.getCases(c.CustomerID)])
			.then(([noteData, casesData]) => {
				if (notes.length === 0) setNotes(noteData)
				setPdfNotes(noteData)
				setSelectedNoteIDs(new Set(noteData.map((n) => n.ID)))
				setPdfData(extractCasePDFData(casesData, c.CaseID, c.CustomerName, c.CustomerRef))
			})
			.catch(() => notification.error("Failed to load case data"))
			.finally(() => setPdfNotesLoading(false))
	}

	const toggleNote = (id: string) => {
		setSelectedNoteIDs((prev) => {
			const next = new Set(prev)
			if (next.has(id)) {
				next.delete(id)
			} else {
				next.add(id)
			}
			return next
		})
	}

	const handleExport = async () => {
		if (!pdfData) return
		setExporting(true)
		try {
			const selected = pdfNotes.filter((n) => selectedNoteIDs.has(n.ID))
			const pdfBlob = await pdf(
				<CasePDFDocument data={{...pdfData, Description: pdfDescription}} notes={selected} />,
			).toBlob()

			const zip = new JSZip()
			const slug = `case-${pdfData.CaseType.toLowerCase()
				.replace(/[^a-z0-9]+/g, "-")
				.replace(/^-|-$/g, "")}-${pdfData.CustomerRef}`
			zip.file("case-summary.pdf", pdfBlob)

			const uploadFetches = selected.flatMap((note) =>
				note.Uploads.map(async (upload) => {
					const baseURL = api.customer.getNoteUploadContentURL(c.CustomerID, note.ID, upload.ID)
					const url = `${baseURL}?AgentEmail=${encodeURIComponent(agentEmail ?? "")}`
					const blob = await fetch(url).then((r) => r.blob())
					zip.file(`uploads/${upload.FileName}`, blob)
				}),
			)
			await Promise.all(uploadFetches)

			const zipBlob = await zip.generateAsync({type: "blob"})
			const link = document.createElement("a")
			link.href = URL.createObjectURL(zipBlob)
			link.download = `${slug}.zip`
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)
			URL.revokeObjectURL(link.href)
			setPdfDialogOpen(false)
			setPdfDescription("")
		} catch {
			notification.error("Failed to export case")
		} finally {
			setExporting(false)
		}
	}

	const handleCopyLink = (e: React.MouseEvent) => {
		e.stopPropagation()
		const url = `${window.location.origin}/customers/${c.CustomerID}/cases?CaseID=${c.CaseID}`
		navigator.clipboard.writeText(url)
		notification.success("Link copied")
	}

	const expiryDateLabel = c.ExpiryDate ? format(new Date(c.ExpiryDate), "d MMM yyyy") : null

	return (
		<>
			<CaseCardContainer
				onClick={() => window.open(`/customers/${c.CustomerID}/cases?CaseID=${c.CaseID}`, "_blank")}
			>
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
					<Box display="flex" alignItems="center" gap={1}>
						<CaseTypeChip label={c.CaseType} />
						<Tooltip title={format(new Date(c.SubmittedOn), "d MMM yyyy")}>
							<Box display="flex" alignItems="center" gap={0.5} color="text.secondary">
								<AccessTimeIcon fontSize="small" />
								<Typography variant="body2" color="textSecondary">
									<Box component="span" color={colors.gray["700"]}>
										Opened
									</Box>{" "}
									{formatDistanceToNow(new Date(c.SubmittedOn), {addSuffix: true})}
								</Typography>
							</Box>
						</Tooltip>
						{pickedUpAt && (
							<Tooltip title={format(new Date(pickedUpAt), "d MMM yyyy")}>
								<Box display="flex" alignItems="center" gap={0.5} color="text.secondary">
									<AccessTimeIcon fontSize="small" />
									<Typography variant="body2" color="textSecondary">
										<Box component="span" color={colors.gray["700"]}>
											Picked up
										</Box>{" "}
										{formatDistanceToNow(new Date(pickedUpAt), {addSuffix: true})}
									</Typography>
								</Box>
							</Tooltip>
						)}
					</Box>
					<Box display="flex" alignItems="center" gap={1}>
						<Tooltip title="Copy link">
							<IconButton size="small" onClick={handleCopyLink}>
								<LinkIcon fontSize="small" />
							</IconButton>
						</Tooltip>
						<Tooltip title="Export">
							<IconButton size="small" onClick={openPdfDialog}>
								<FileDownloadIcon fontSize="small" />
							</IconButton>
						</Tooltip>
						<AddReminderButton customerID={c.CustomerID} caseID={c.CaseID} caseType={c.CaseType} />
						{c.ExpireInDays !== null && (
							<Tooltip
								title={expiryDateLabel ? `Expires on ${expiryDateLabel}` : "No expiry set"}
								placement="bottom-end"
							>
								<ExpiryChip
									label={`Expires in ${c.ExpireInDays}d`}
									size="small"
									variant="outlined"
									$urgency={
										c.ExpireInDays <= 3 ? "critical" : c.ExpireInDays <= 7 ? "soon" : "normal"
									}
								/>
							</Tooltip>
						)}
						<ChevronRightIcon sx={{color: colors.gray["400"], fontSize: 20}} />
					</Box>
				</Box>

				<Typography variant="body2" color="textSecondary" mb={1}>
					{c.ClientName} ({c.CountryCode})
				</Typography>

				<Typography variant="subtitle1" fontWeight={600}>
					{c.CustomerName}
				</Typography>
				<Typography variant="body2" color="textSecondary" mb={1.5}>
					{c.CustomerRef}
				</Typography>

				<Accordion
					disableGutters
					elevation={0}
					sx={{
						border: `1px solid ${colors.gray["200"]}`,
						borderRadius: "4px",
						"&::before": {display: "none"},
					}}
					onClick={(e) => e.stopPropagation()}
					onChange={(_, expanded) => {
						if (expanded) loadNotes()
					}}
				>
					<AccordionSummary expandIcon={<ExpandMoreIcon />}>
						<Typography variant="body2" color="textSecondary">
							Notes
						</Typography>
					</AccordionSummary>
					<AccordionDetails sx={{pt: 0}}>
						{notesLoading ? (
							<Box display="flex" flexDirection="column" gap={2.5}>
								{[0, 1].map((i) => (
									<Box key={i} p={4} border="1px solid" borderColor="grey.200" borderRadius={1}>
										<Box display="flex" justifyContent="space-between" mb={1.5}>
											<Skeleton variant="text" width={160} />
											<Skeleton variant="rounded" width={72} height={20} />
										</Box>
										<Skeleton variant="text" width="90%" />
										<Skeleton variant="text" width="75%" />
									</Box>
								))}
							</Box>
						) : notes.length === 0 ? (
							<Typography variant="body2" color="textSecondary">
								No notes
							</Typography>
						) : (
							<Box display="flex" flexDirection="column" gap={2.5}>
								{notes.map((note) => (
									<NoteItemCard
										key={note.ID}
										note={note}
										attachmentsEnabled
										alwaysShowFieldName
										onArchived={loadNotes}
									/>
								))}
							</Box>
						)}
					</AccordionDetails>
				</Accordion>
			</CaseCardContainer>

			<Dialog
				open={pdfDialogOpen}
				onClose={() => {
					setPdfDialogOpen(false)
					setPdfDescription("")
				}}
				onClick={(e) => e.stopPropagation()}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>Export</DialogTitle>
				<DialogContent>
					<TextField
						placeholder="Add a message or description to include at the top of the PDF..."
						multiline
						minRows={3}
						fullWidth
						size="small"
						value={pdfDescription}
						onChange={(e) => setPdfDescription(e.target.value)}
						sx={{mb: 3}}
					/>
					<Typography variant="body2" color="text.secondary" mb={2}>
						Select the notes to include in the PDF.
					</Typography>
					{pdfNotesLoading ? (
						<Box display="flex" justifyContent="center" py={2}>
							<CircularProgress size={24} />
						</Box>
					) : pdfNotes.length === 0 ? (
						<Typography variant="body2" color="text.secondary">
							No notes on this case.
						</Typography>
					) : (
						<Box display="flex" flexDirection="column">
							{pdfNotes.map((note, i) => (
								<Box key={note.ID}>
									{i > 0 && <Divider sx={{my: 1}} />}
									<FormControlLabel
										sx={{alignItems: "flex-start"}}
										control={
											<Checkbox
												size="small"
												checked={selectedNoteIDs.has(note.ID)}
												onChange={() => toggleNote(note.ID)}
												sx={{pt: 0.5}}
											/>
										}
										label={
											<Box>
												<Typography variant="body2" fontWeight={500}>
													{note.Label}
												</Typography>
												<Typography
													variant="caption"
													color="text.secondary"
													display="block"
													mb={1}
												>
													{note.AgentName} ·{" "}
													{format(new Date(note.CreatedAt), "d MMM yyyy, HH:mm")}
												</Typography>
												{note.Form.Fields.filter((f) => f.Content).map((field) => (
													<Box key={field.Name} mb={0.5}>
														<Typography
															variant="caption"
															color="text.secondary"
															display="block"
															fontWeight={600}
															sx={{textTransform: "uppercase", fontSize: "0.65rem"}}
														>
															{field.Name}
														</Typography>
														<Typography
															variant="caption"
															display="block"
															color="text.primary"
														>
															{field.Content}
														</Typography>
													</Box>
												))}
											</Box>
										}
									/>
								</Box>
							))}
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							setPdfDialogOpen(false)
							setPdfDescription("")
						}}
						size="small"
					>
						Cancel
					</Button>
					<Button
						variant="contained"
						size="small"
						disabled={exporting || pdfNotesLoading || !pdfData}
						onClick={handleExport}
						startIcon={
							exporting ? (
								<CircularProgress size={14} color="inherit" />
							) : (
								<FileDownloadIcon fontSize="small" />
							)
						}
					>
						Export
					</Button>
				</DialogActions>
			</Dialog>
		</>
	)
}

function extractCasePDFData(
	casesData: CasesResponse,
	caseID: string,
	customerName: string,
	customerRef: string,
): CasePDFData {
	const active = casesData.ActiveCases.find((c) => c.CaseID === caseID)
	if (active) {
		return {
			CaseType: active.CaseType,
			CustomerName: customerName,
			CustomerRef: customerRef,
			SubmittedAt: active.SubmittedAt,
			ExpiryDate: active.ExpiryDate || null,
			Accounts: active.Accounts.map((a) => ({
				AccountRef: a.AccountRef,
				Balance: a.Balance,
				Resolution: a.Resolution,
			})),
		}
	}
	const closed = casesData.ClosedCases.find((c) => c.CaseID === caseID)
	if (closed) {
		return {
			CaseType: closed.CaseType,
			CustomerName: customerName,
			CustomerRef: customerRef,
			SubmittedAt: closed.SubmittedAt,
			ExpiryDate: closed.ExpiryDate || null,
			Accounts: [],
		}
	}
	const resolvedAccount = casesData.ResolvedCases?.AccountCases?.find((c) => c.CaseID === caseID)
	if (resolvedAccount) {
		return {
			CaseType: resolvedAccount.CaseType,
			CustomerName: customerName,
			CustomerRef: customerRef,
			SubmittedAt: resolvedAccount.SubmittedAt,
			ExpiryDate: resolvedAccount.ExpiryDate || null,
			Accounts: resolvedAccount.Accounts.map((a) => ({
				AccountRef: a.AccountRef,
				Balance: a.Balance,
				Resolution: a.Resolution,
			})),
		}
	}
	return {
		CaseType: "",
		CustomerName: customerName,
		CustomerRef: customerRef,
		SubmittedAt: "",
		ExpiryDate: null,
		Accounts: [],
	}
}

const CaseCardContainer = styled.div`
	border-radius: 8px;
	padding: 16px;
	cursor: pointer;
	box-shadow:
		0px 3px 1px -2px rgba(0, 0, 0, 0.2),
		0px 2px 2px 0px rgba(0, 0, 0, 0.14),
		0px 1px 5px 0px rgba(0, 0, 0, 0.12);
	transition: box-shadow 0.2s;

	&:hover {
		box-shadow:
			0px 3px 3px -2px rgba(66, 103, 178, 0.2),
			0px 4px 6px 0px rgba(66, 103, 178, 0.14),
			0px 2px 8px 1px rgba(66, 103, 178, 0.12);
	}
`

const CaseTypeChip = styled(Chip).attrs({size: "small", variant: "outlined"})`
	&& {
		background-color: ${colors.warning["50"]};
		border-color: ${colors.warning["200"]};
		color: ${colors.warning["800"]};
	}
	font-weight: 500;
`

const ExpiryChip = styled(Chip)<{$urgency: "critical" | "soon" | "normal"}>`
	&& {
		background-color: ${({$urgency}) =>
			$urgency === "critical"
				? colors.error["50"]
				: $urgency === "soon"
					? colors.warning["50"]
					: colors.gray["50"]};
		border-color: ${({$urgency}) =>
			$urgency === "critical"
				? colors.error["200"]
				: $urgency === "soon"
					? colors.warning["200"]
					: colors.gray["300"]};
		color: ${({$urgency}) =>
			$urgency === "critical"
				? colors.error["700"]
				: $urgency === "soon"
					? colors.warning["800"]
					: colors.gray["600"]};
	}
	font-weight: 500;
`

export {CasesPage}
